/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */
import { z as zod } from "zod"

/**
 * Create a meet login — a Google Workspace user identity attached to a parent meet workspace.

    Each login represents one Workspace user that bots can sign in as. Many logins can share one workspace; they all use the workspace's cert/key for SAML signing. Round-robin assignment picks the least-loaded active login when a bot is dispatched with `meet_config.email_group` (or `credential_id`) on `POST /v2/bots`.

    **Prerequisites:** Create a meet workspace first via `POST /v2/meet-workspaces`. Pass the returned `workspace_id` here.

    **Domain Validation:** The `email` must belong to the parent workspace's domain (or a subdomain). For example, if the workspace domain is `bots.acme.com`, valid emails include `bot1@bots.acme.com` or `bot2@dev.bots.acme.com`, but not `bot1@acme.com`. The same rule applies to `email_group` if provided.

    **Email Group (optional but encouraged):** Set `email_group` to a Google Group address that contains the bot users as members. When you put this group address on a calendar invite, the assigned bot lands in Meet's verified queue and bypasses the waiting room. Logins sharing the same `email_group` form one round-robin pool.

    **Per-team Uniqueness on Email:** Each `email` may exist at most once per team. Attempting to register the same email twice returns 409.

    **Concurrency:** Each login supports up to 20 concurrent SSO sessions by default (configurable per environment). When a login hits its capacity, the round-robin assigner skips it and tries the next one in the pool. If all logins in a pool are saturated, the bot creation fails with `MEET_LOGIN_UNAVAILABLE` (or falls back to anonymous, depending on your `fallback` setting).

    **Error Scenarios:**
    - `404 Not Found`: `workspace_id` is unknown or does not belong to your team.
    - `409 Conflict`: A login for this `email` already exists.
    - `422 Unprocessable Entity`: `email` or `email_group` domain does not match the parent workspace's domain.
 * @summary Create a meet login
 */
export const createMeetLoginBodyWorkspaceIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const createMeetLoginBodyNameMax = 100
export const createMeetLoginBodyEmailMax = 254

export const createMeetLoginBodyEmailRegExp =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/
export const createMeetLoginBodyEmailGroupMax = 254

export const createMeetLoginBodyEmailGroupRegExp =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/

export const createMeetLoginBody = zod.object({
  workspace_id: zod
    .string()
    .uuid()
    .regex(createMeetLoginBodyWorkspaceIdRegExp)
    .describe("UUID of the parent meet_workspace. Create one via POST /v2/meet-workspaces first."),
  name: zod
    .string()
    .min(1)
    .max(createMeetLoginBodyNameMax)
    .describe(
      "User-friendly label for this credential. Not unique — names can repeat within a team."
    ),
  email: zod
    .string()
    .email()
    .max(createMeetLoginBodyEmailMax)
    .regex(createMeetLoginBodyEmailRegExp)
    .describe(
      "Email of the Google Workspace user this login signs in as. Domain must match the parent workspace's domain."
    ),
  email_group: zod
    .string()
    .email()
    .max(createMeetLoginBodyEmailGroupMax)
    .regex(createMeetLoginBodyEmailGroupRegExp)
    .optional()
    .describe(
      "Optional Google Group address used for round-robin pooling. Logins sharing the same email_group form one round-robin pool. If you put this group on calendar invites, the assigned bot will land in the verified queue and bypass the waiting room."
    ),
  extra: zod
    .record(zod.string(), zod.any())
    .describe(
      "An optional free-form JSON object you can attach to the login.\n\nStored as-is and never interpreted by the API. Useful for tagging logins with your own identifiers (e.g. internal user ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
    )
    .or(zod.null())
    .optional()
})

/**
 * List all meet logins for your team across every workspace.

    Each row includes its parent `workspace_id` so you can correlate logins to their workspace without re-fetching. Sensitive fields (cert, key) live on the workspace, not the login, and are never returned through this endpoint.

    **State Field:** Logins can independently be `active` or `invalid`. A login flipping to `invalid` is rare in v1 — most SSO failures are workspace-scoped (cert mismatch) and disable the parent workspace instead. If a specific login is misbehaving (e.g., the Workspace user got suspended), you can manually delete and re-create it.

    **Filtering by workspace:** Not currently supported as a query param. Filter client-side using the `workspace_id` field on each row, or fetch the workspace and use `GET /v2/meet-workspaces/:id` for inventory.

    **Round-Robin Tracking:** `active_session_count` (current concurrent bots using this login) and `last_used_at` (last assignment timestamp) reflect the round-robin scheduler's view. Use these to sanity-check load distribution.
 * @summary List meet logins
 */
export const listMeetLoginsQueryExtraDefault = null

export const listMeetLoginsQueryParams = zod.object({
  extra: zod
    .string()
    .describe(
      'Filter logins by matching values in the `extra` JSON payload.\n\nFormat: `\"field:value\"` for single condition or `\"f1:v1,f2:v2\"` for multiple (AND).\nValues are exact-match (case-sensitive). Logins without the field are excluded.'
    )
    .or(zod.null())
    .optional()
})

export const listMeetLoginsResponseDataItemCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const listMeetLoginsResponseDataItemActiveSessionCountMin = 0

export const listMeetLoginsResponseDataItemActiveSessionCountMax = 9007199254740991

export const listMeetLoginsResponse = zod.object({
  success: zod.literal(true),
  data: zod.array(
    zod.object({
      credential_id: zod
        .string()
        .uuid()
        .regex(listMeetLoginsResponseDataItemCredentialIdRegExp)
        .describe("The unique identifier (UUID) of the meet login"),
      workspace_id: zod.string(),
      name: zod.string(),
      email: zod.string(),
      email_group: zod.string().or(zod.null()),
      state: zod
        .enum(["active", "invalid"])
        .describe(
          "The current state of the meet login.\n\n- `active`: Login is healthy and can be assigned to bots\n- `invalid`: System auto-flipped this on bot login failure (suspended user, first-time interactive login required, etc.). Manual re-enable required."
        ),
      failure_data: zod.any().optional(),
      last_error_message: zod.string().or(zod.null()),
      last_error_at: zod.string().or(zod.null()),
      last_used_at: zod.string().or(zod.null()),
      active_session_count: zod
        .number()
        .min(listMeetLoginsResponseDataItemActiveSessionCountMin)
        .max(listMeetLoginsResponseDataItemActiveSessionCountMax),
      extra: zod
        .record(zod.string(), zod.any())
        .describe(
          "An optional free-form JSON object you can attach to the login.\n\nStored as-is and never interpreted by the API. Useful for tagging logins with your own identifiers (e.g. internal user ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
        )
        .or(zod.null()),
      created_at: zod.string(),
      updated_at: zod.string()
    })
  )
})

/**
 * Get current concurrency utilization for your team's meet login pool.

    Returns aggregate metrics across all your active logins:

    - `logins_total`: total number of logins for your team
    - `logins_active`: number with `state: "active"`
    - `logins_invalid`: number with `state: "invalid"`
    - `concurrent_sessions`: current sum of `active_session_count` across active logins (i.e., bots in flight using your auth pool)
    - `concurrent_capacity`: `logins_active × per-login-capacity` (defaults to 20 per login)
    - `utilization_pct`: `concurrent_sessions / concurrent_capacity` as a percentage
    - `by_email_group`: per-pool breakdown of the same metrics

    **Use this with alert rules.** Configure a `meet_login_utilization` threshold alert at e.g. 70% to get notified before saturation, and configure `meet_login_unavailable` operational alerts to know when bots have actually hit the ceiling.

    **Polling:** Cheap to call. Numbers reflect live counters — no caching.
 * @summary Get current login pool utilization
 */
export const getMeetLoginUtilizationResponseDataLoginsTotalMin = 0

export const getMeetLoginUtilizationResponseDataLoginsTotalMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataLoginsActiveMin = 0

export const getMeetLoginUtilizationResponseDataLoginsActiveMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataLoginsInvalidMin = 0

export const getMeetLoginUtilizationResponseDataLoginsInvalidMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataConcurrentSessionsMin = 0

export const getMeetLoginUtilizationResponseDataConcurrentSessionsMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataConcurrentCapacityMin = 0

export const getMeetLoginUtilizationResponseDataConcurrentCapacityMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataByEmailGroupItemLoginsMin = 0

export const getMeetLoginUtilizationResponseDataByEmailGroupItemLoginsMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataByEmailGroupItemConcurrentMin = 0

export const getMeetLoginUtilizationResponseDataByEmailGroupItemConcurrentMax = 9007199254740991
export const getMeetLoginUtilizationResponseDataByEmailGroupItemCapacityMin = 0

export const getMeetLoginUtilizationResponseDataByEmailGroupItemCapacityMax = 9007199254740991

export const getMeetLoginUtilizationResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    logins_total: zod
      .number()
      .min(getMeetLoginUtilizationResponseDataLoginsTotalMin)
      .max(getMeetLoginUtilizationResponseDataLoginsTotalMax),
    logins_active: zod
      .number()
      .min(getMeetLoginUtilizationResponseDataLoginsActiveMin)
      .max(getMeetLoginUtilizationResponseDataLoginsActiveMax),
    logins_invalid: zod
      .number()
      .min(getMeetLoginUtilizationResponseDataLoginsInvalidMin)
      .max(getMeetLoginUtilizationResponseDataLoginsInvalidMax),
    concurrent_sessions: zod
      .number()
      .min(getMeetLoginUtilizationResponseDataConcurrentSessionsMin)
      .max(getMeetLoginUtilizationResponseDataConcurrentSessionsMax),
    concurrent_capacity: zod
      .number()
      .min(getMeetLoginUtilizationResponseDataConcurrentCapacityMin)
      .max(getMeetLoginUtilizationResponseDataConcurrentCapacityMax),
    utilization_pct: zod.number(),
    by_email_group: zod.array(
      zod.object({
        email_group: zod.string().or(zod.null()),
        logins: zod
          .number()
          .min(getMeetLoginUtilizationResponseDataByEmailGroupItemLoginsMin)
          .max(getMeetLoginUtilizationResponseDataByEmailGroupItemLoginsMax),
        concurrent: zod
          .number()
          .min(getMeetLoginUtilizationResponseDataByEmailGroupItemConcurrentMin)
          .max(getMeetLoginUtilizationResponseDataByEmailGroupItemConcurrentMax),
        capacity: zod
          .number()
          .min(getMeetLoginUtilizationResponseDataByEmailGroupItemCapacityMin)
          .max(getMeetLoginUtilizationResponseDataByEmailGroupItemCapacityMax)
      })
    )
  })
})

/**
 * Get full details for a single meet login.

    Returns the same fields as the list endpoint, scoped to one login.

    Returns 404 if the login is not found or does not belong to your team.
 * @summary Get a meet login
 */
export const getMeetLoginPathCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const getMeetLoginParams = zod.object({
  credential_id: zod
    .string()
    .uuid()
    .regex(getMeetLoginPathCredentialIdRegExp)
    .describe("The unique identifier (UUID) of the meet login")
})

export const getMeetLoginResponseDataCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const getMeetLoginResponseDataActiveSessionCountMin = 0

export const getMeetLoginResponseDataActiveSessionCountMax = 9007199254740991

export const getMeetLoginResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    credential_id: zod
      .string()
      .uuid()
      .regex(getMeetLoginResponseDataCredentialIdRegExp)
      .describe("The unique identifier (UUID) of the meet login"),
    workspace_id: zod.string(),
    name: zod.string(),
    email: zod.string(),
    email_group: zod.string().or(zod.null()),
    state: zod
      .enum(["active", "invalid"])
      .describe(
        "The current state of the meet login.\n\n- `active`: Login is healthy and can be assigned to bots\n- `invalid`: System auto-flipped this on bot login failure (suspended user, first-time interactive login required, etc.). Manual re-enable required."
      ),
    failure_data: zod.any().optional(),
    last_error_message: zod.string().or(zod.null()),
    last_error_at: zod.string().or(zod.null()),
    last_used_at: zod.string().or(zod.null()),
    active_session_count: zod
      .number()
      .min(getMeetLoginResponseDataActiveSessionCountMin)
      .max(getMeetLoginResponseDataActiveSessionCountMax),
    extra: zod
      .record(zod.string(), zod.any())
      .describe(
        "An optional free-form JSON object you can attach to the login.\n\nStored as-is and never interpreted by the API. Useful for tagging logins with your own identifiers (e.g. internal user ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
      )
      .or(zod.null()),
    created_at: zod.string(),
    updated_at: zod.string()
  })
})

/**
 * Update a meet login — rename it, change its email_group, or re-enable it after auto-disable.

    **Rename:** pass only `name`.

    **Update email_group:** pass `email_group` to change the round-robin pool the login belongs to. Pass an empty string to clear it (the login becomes pool-less and is only assignable via explicit `credential_id`). The new `email_group` value must satisfy the same domain rule as create — its domain must match (or be a subdomain of) the parent workspace's domain.

    **Re-enable after auto-disable:** pass `state: "active"`. Only valid if the login is currently `invalid`. Clears `failure_data`, `last_error_message`, and `last_error_at`.

    `email` and `workspace_id` are immutable. To change either, delete the login and create a new one.

    **State Restrictions:** PATCH cannot set `state` to `invalid` — that's system-only.

    **Error Scenarios:**
    - `400 Bad Request`: Attempted to set `state` to a value other than `active`.
    - `404 Not Found`: Login not found or does not belong to your team.
    - `422 Unprocessable Entity`: `email_group` domain does not match the parent workspace's domain.
 * @summary Update a meet login
 */
export const updateMeetLoginPathCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const updateMeetLoginParams = zod.object({
  credential_id: zod
    .string()
    .uuid()
    .regex(updateMeetLoginPathCredentialIdRegExp)
    .describe("The unique identifier (UUID) of the meet login")
})

export const updateMeetLoginBodyNameMax = 100
export const updateMeetLoginBodyEmailGroupMaxOne = 254
export const updateMeetLoginBodyEmailGroupRegExpOne =
  /^(?!\.)(?!.*\.\.)([A-Za-z0-9_'+\-.]*)[A-Za-z0-9_+-]@([A-Za-z0-9][A-Za-z0-9-]*\.)+[A-Za-z]{2,}$/

export const updateMeetLoginBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(updateMeetLoginBodyNameMax)
    .optional()
    .describe(
      "User-friendly label for this credential. Not unique — names can repeat within a team."
    ),
  email_group: zod
    .string()
    .email()
    .max(updateMeetLoginBodyEmailGroupMaxOne)
    .regex(updateMeetLoginBodyEmailGroupRegExpOne)
    .describe(
      "Optional Google Group address used for round-robin pooling. Logins sharing the same email_group form one round-robin pool. If you put this group on calendar invites, the assigned bot will land in the verified queue and bypass the waiting room."
    )
    .or(zod.enum([""]))
    .optional(),
  state: zod
    .enum(["active"])
    .optional()
    .describe(
      "Only 'active' is allowed via PATCH (system-only sets 'invalid'). Use this to re-enable a login after fixing the underlying failure."
    ),
  extra: zod
    .record(zod.string(), zod.any())
    .describe(
      "An optional free-form JSON object you can attach to the login.\n\nStored as-is and never interpreted by the API. Useful for tagging logins with your own identifiers (e.g. internal user ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
    )
    .or(zod.null())
    .optional()
})

export const updateMeetLoginResponseDataCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/
export const updateMeetLoginResponseDataActiveSessionCountMin = 0

export const updateMeetLoginResponseDataActiveSessionCountMax = 9007199254740991

export const updateMeetLoginResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    credential_id: zod
      .string()
      .uuid()
      .regex(updateMeetLoginResponseDataCredentialIdRegExp)
      .describe("The unique identifier (UUID) of the meet login"),
    workspace_id: zod.string(),
    name: zod.string(),
    email: zod.string(),
    email_group: zod.string().or(zod.null()),
    state: zod
      .enum(["active", "invalid"])
      .describe(
        "The current state of the meet login.\n\n- `active`: Login is healthy and can be assigned to bots\n- `invalid`: System auto-flipped this on bot login failure (suspended user, first-time interactive login required, etc.). Manual re-enable required."
      ),
    failure_data: zod.any().optional(),
    last_error_message: zod.string().or(zod.null()),
    last_error_at: zod.string().or(zod.null()),
    last_used_at: zod.string().or(zod.null()),
    active_session_count: zod
      .number()
      .min(updateMeetLoginResponseDataActiveSessionCountMin)
      .max(updateMeetLoginResponseDataActiveSessionCountMax),
    extra: zod
      .record(zod.string(), zod.any())
      .describe(
        "An optional free-form JSON object you can attach to the login.\n\nStored as-is and never interpreted by the API. Useful for tagging logins with your own identifiers (e.g. internal user ID, environment) so you can correlate them in your application or filter them via the list endpoint.\n\nFilterable on the list endpoint via the `extra` query parameter using the `key:value` syntax."
      )
      .or(zod.null()),
    created_at: zod.string(),
    updated_at: zod.string()
  })
})

/**
 * Delete a meet login.

    **In-Use Guard:** If the login has any active SSO sessions in flight (`active_session_count > 0`), the request returns 409. Wait for the bots to finish or stop them first.

    **Bot Reference Handling:** Bots that previously used this login keep their reference in `assigned_meet_login_id` until the row is deleted, then the FK is set to `null` (no historical bot record is removed; just the link).

    Returns 404 if the login is not found or does not belong to your team.
 * @summary Delete a meet login
 */
export const deleteMeetLoginPathCredentialIdRegExp =
  /^([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-8][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/

export const deleteMeetLoginParams = zod.object({
  credential_id: zod
    .string()
    .uuid()
    .regex(deleteMeetLoginPathCredentialIdRegExp)
    .describe("The unique identifier (UUID) of the meet login")
})

export const deleteMeetLoginResponse = zod.object({
  success: zod.literal(true),
  data: zod.object({
    message: zod.string()
  })
})
